import memeService from "../../services/MemeService";
import globalConstants from "../../globals/GlobalConstants";
import useWebSocket from "react-use-websocket";
import Source from "./Source";
import "./source.css";
import { useEffect, useState } from "react";
let audio = new Audio("/memeNotify.mp3");

const SourceContainer = () => {
    document.body.style = "background: none;";
    const [isDisplaying, setIsDisplaying] = useState(false);
    const [keepAliveInterval, setKeepAliveInterval] = useState(null);
    const [hadSuccesfulConnection, setHadSuccesfulConnection] = useState(false);
    const [
        reconnectionAttemptsFirstConnection,
        setReconnectionAttemptsFirstConnection,
    ] = useState(0);
    const [retryOnErrorForWebSocket, setRetryOnErrorForWebSocket] =
        useState(true);
    const [memeObject, setMemeObject] = useState(null);
    const [memequeue, setMemequeue] = useState([]);
    const [userSettings, setUserSettings] = useState({
        browserSourceInterval: 10000,
        browserSourceNotifySound: 0,
        browserSourceTimeOut: 10000,
    });

    const onShouldReconnect = () => {
        const firstConnectionRetryThreshold = 5;
        const maxRetriesReached =
            reconnectionAttemptsFirstConnection >=
            firstConnectionRetryThreshold;

        if (!hadSuccesfulConnection) {
            if (maxRetriesReached) {
                // Max retries reached, stop reconnection attempts
                setRetryOnErrorForWebSocket(false);
                clearInterval(keepAliveInterval); // Clear intervals to avoid unnecessary calls
                console.error(
                    "Browser source could not establish a connection!",
                );
                return false;
            }

            // Retry connection if under threshold
            setReconnectionAttemptsFirstConnection(
                (prevAttempts) => prevAttempts + 1,
            );
            console.warn(
                `Attempting to connect ${reconnectionAttemptsFirstConnection + 1}/${firstConnectionRetryThreshold}`,
            );
        }

        return true;
    };

    const onOpenConnection = () => {
        console.log("connected");
        setHadSuccesfulConnection(true);
        setReconnectionAttemptsFirstConnection(0);
        setRetryOnErrorForWebSocket(true);
    };

    const onMsg = async (event) => {
        try {
            const eventData = JSON.parse(event.data);

            // Validate that eventData contains the expected fields
            const isValidMemeEvent =
                eventData?.message === "DISPLAY_MEME" && eventData?.data;

            if (isValidMemeEvent)
                setMemequeue((prevQueue) => [...prevQueue, eventData.data]);
            else console.warn("onMsg -> Invalid event data:", eventData);
        } catch (error) {
            console.error("onMsg -> Error parsing event data:", error);
        }
    };

    const webSocketOptions = {
        onOpen: onOpenConnection,
        onMessage: onMsg,
        onError: console.error,
        //Will attempt to reconnect on all close events
        shouldReconnect: onShouldReconnect,
        retryOnError: retryOnErrorForWebSocket,
        // retries to connect every 10 seconds for up to 2 days
        // if the first connection was successful
        reconnectInterval: 10000,
        reconnectAttempts: 17280,
    };

    const websocketConnectionURL = `${globalConstants.webSocketUrl}?source=${window.location.pathname.replace(/\/source\//g, "")}`;
    const { sendMessage } = useWebSocket(
        websocketConnectionURL,
        webSocketOptions,
    );

    const sleep = (ms) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
    };

    useEffect(() => {
        const handleQueueUpdate = async () => {
            if (!memequeue.length || memeObject || isDisplaying) return;

            // Play audio if it's not already playing
            try {
                const playPromise = audio.play();
                if (playPromise !== undefined) {
                    await playPromise;
                }
            } catch (error) {
                console.error(
                    "Audio playback failed: User interaction required to play sound.",
                );
            }

            let meme = memequeue[0];
            setMemequeue((prevQueue) =>
                prevQueue.filter((memeInQueue) => memeInQueue.id !== meme.id),
            );

            setIsDisplaying(true);
            setMemeObject(meme);

            //display the image
            await sleep(userSettings.browserSourceInterval);

            // Notify meme displayed
            try {
                await memeService.memeDisplayed(meme.id);
            } catch {
                console.error("Meme transition error!");
            }

            // Update settings if meme has settings, otherwise log error
            if (meme.settings && meme.settings !== userSettings) {
                setUserSettings(meme.settings);
            } else if (!meme.settings) {
                console.error(meme, "settings missing!");
            }

            setMemeObject(null);

            // Before we set the queue again, we sleep a couple of seconds
            await sleep(1500);
            setIsDisplaying(false);
        };

        handleQueueUpdate();
    }, [memequeue, memeObject, userSettings, isDisplaying]);

    // This loads only on first render
    useEffect(() => {
        const loadUserSettings = async () => {
            try {
                console.log("Getting settings");

                const response = await memeService.getSourceSettings(
                    window.location.pathname.replace("/source/", ""),
                );

                if (response.ok) {
                    const settings = await response.json();
                    setUserSettings(settings);
                } else console.error("Error loading settings: Response not ok");
            } catch (error) {
                console.error("Error loading settings:", error);
            }
        };

        loadUserSettings();
    }, []);

    useEffect(() => {
        if (!keepAliveInterval) {
            const interval = setInterval(
                () => sendMessage({ message: "keep-alive" }),
                60000 * 9,
            );
            setKeepAliveInterval(interval);
        }

        return () => clearInterval(keepAliveInterval);
    }, [sendMessage, keepAliveInterval]);

    return (
        <div className="sourceContainer">
            <Source memeObject={memeObject} />
        </div>
    );
};

export default SourceContainer;
